import * as R from "ramda";
import * as React from "react";
import { FaBookOpen, FaFileAlt, FaHome, FaTags } from "react-icons/fa";
import { IconType } from "react-icons/lib/esm/iconBase";
import styled from "styled-components";

import { courseInstructionViews } from "../../../../utils/courseViews";
import { PreferredLocale } from "../../../../utils/locales";
import { findComponent } from "../../../course-author/helpers/patches/util";
import {
  CourseComponentTag,
  CourseInstructionType,
  CourseUnitView,
  CourseView,
  ICourseEditOperations,
  ICourseInstruction,
  ICourseNavigationOperations,
  ICourseUnit,
  IDisplayGroups,
  IFullCourse,
  Slide,
} from "../../../course-author/types";
import CourseComponentList from "../CourseComponentsList";
import NewComponentLink from "../NewComponentLink";
import ComponentLink from "./ComponentLink";

const StyledNavSectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const StyledNavSectionTitleText = styled.span`
  padding-left: 1.2rem;
`;
interface INavSectionTitleProps {
  iconElement: IconType;
  title: string;
}

export const NavSectionTitle: React.FC<INavSectionTitleProps> = ({ iconElement: IconElement, title }) => (
  <StyledNavSectionTitle>
    <IconElement />
    <StyledNavSectionTitleText>{title}</StyledNavSectionTitleText>
  </StyledNavSectionTitle>
);

interface IInstructionList {
  course: IFullCourse;
  createComponent: () => void;
  currentLocale: PreferredLocale;
  deleteComponent: (path: CourseUnitView[], instr: ICourseInstruction) => () => void;
  displayedComponent: Slide | null;
  headerIcon: IconType;
  instructions: ICourseInstruction[];
  setDisplayedComponent: (instr: ICourseInstruction) => () => void;
  type: string;
  unitPath: CourseUnitView[];
}

const instructionListTitle = (headerIcon: IconType, type: string) => () => (
  <NavSectionTitle iconElement={headerIcon} title={type} />
);

const InstructionList: React.FC<IInstructionList> = ({
  course,
  unitPath,
  createComponent,
  currentLocale,
  deleteComponent,
  displayedComponent,
  headerIcon,
  instructions,
  setDisplayedComponent,
  type,
}) => {
  return (
    <CourseComponentList renderHeader={instructionListTitle(headerIcon, type)}>
      {instructions.map((instr, i) => {
        return (
          <ComponentLink
            component={instr}
            currentLocale={currentLocale}
            deleteFn={deleteComponent(unitPath, instr)}
            key={i}
            onClick={setDisplayedComponent(instr)}
            selected={instr === displayedComponent}
            to={`?position=${findComponent(course, unitPath, instr)}`}
            type={instr.content.type}
          />
        );
      })}
      <NewComponentLink title={`New slide`} onClick={createComponent} />
    </CourseComponentList>
  );
};

export interface IInstructionGroupListProps {
  course: IFullCourse;
  currentLocale: PreferredLocale;
  displayedComponent: Slide | null;
  displayGroups: IDisplayGroups;
  editOperations: ICourseEditOperations;
  navigationOperations: ICourseNavigationOperations;
  unitPath: Array<CourseView<ICourseUnit, IFullCourse>>;
}

const InstructionGroupsList: React.FC<IInstructionGroupListProps> = ({
  course,
  currentLocale,
  displayGroups,
  unitPath,
  editOperations: { createComponent, deleteComponent },
  displayedComponent,
  navigationOperations: { setDisplayedComponent },
}) => {
  if (unitPath.length === 0) {
    return null;
  }
  const instructionGroups = R.compose<
    IDisplayGroups,
    ICourseInstruction[],
    ICourseInstruction[],
    ICourseInstruction[],
    { [index: string]: ICourseInstruction[] }
  >(
    R.groupBy((instr: ICourseInstruction) => courseInstructionViews.type(instr) as CourseInstructionType),
    R.filter<ICourseInstruction>(instr => courseInstructionViews.type(instr) !== null),
    R.filter<ICourseInstruction>(instr => !courseInstructionViews.deleted(instr)),
    R.propOr<ICourseInstruction[]>([], CourseComponentTag.Instruction)
  )(displayGroups);

  const createInstruction = (type: CourseInstructionType) =>
    createComponent(unitPath, CourseComponentTag.Instruction, type);

  const appendices = R.propOr<ICourseInstruction[], any, ICourseInstruction[]>(
    [],
    CourseInstructionType.Appendix,
    instructionGroups
  );
  const summaries = R.propOr<ICourseInstruction[], any, ICourseInstruction[]>(
    [],
    CourseInstructionType.Summary,
    instructionGroups
  );
  const lessons = R.propOr<ICourseInstruction[], any, ICourseInstruction[]>(
    [],
    CourseInstructionType.Lesson,
    instructionGroups
  );
  const introductions = R.propOr<ICourseInstruction[], any, ICourseInstruction[]>(
    [],
    CourseInstructionType.Introduction,
    instructionGroups
  );
  return (
    <>
      <InstructionList
        course={course}
        createComponent={createInstruction(CourseInstructionType.Introduction)}
        currentLocale={currentLocale}
        deleteComponent={deleteComponent}
        displayedComponent={displayedComponent}
        headerIcon={FaHome}
        instructions={introductions}
        setDisplayedComponent={setDisplayedComponent}
        type={"Introduction"}
        unitPath={unitPath}
      />
      <InstructionList
        course={course}
        createComponent={createInstruction(CourseInstructionType.Lesson)}
        currentLocale={currentLocale}
        deleteComponent={deleteComponent}
        displayedComponent={displayedComponent}
        headerIcon={FaBookOpen}
        instructions={lessons}
        setDisplayedComponent={setDisplayedComponent}
        type={"Lesson"}
        unitPath={unitPath}
      />
      <InstructionList
        course={course}
        createComponent={createInstruction(CourseInstructionType.Summary)}
        currentLocale={currentLocale}
        deleteComponent={deleteComponent}
        displayedComponent={displayedComponent}
        headerIcon={FaFileAlt}
        instructions={summaries}
        setDisplayedComponent={setDisplayedComponent}
        type={"Summary"}
        unitPath={unitPath}
      />
      <InstructionList
        course={course}
        createComponent={createInstruction(CourseInstructionType.Appendix)}
        currentLocale={currentLocale}
        deleteComponent={deleteComponent}
        displayedComponent={displayedComponent}
        headerIcon={FaTags}
        instructions={appendices}
        setDisplayedComponent={setDisplayedComponent}
        type={"Appendix"}
        unitPath={unitPath}
      />
    </>
  );
};

export default InstructionGroupsList;
